import { Controller } from "@hotwired/stimulus"

// Controller for the regenerate block modal
// Handles opening/closing the modal and submitting regeneration requests
export default class extends Controller {
  static targets = [
    "form", "customPrompt", "optimizationGuidance",
    "loading", "error", "errorMessage", "submitButton"
  ]
  
  static values = {
    blockId: String,
    contentAssetId: String
  }
  
  connect() {
    console.log("RegenerateBlockModalController connected for block:", this.blockIdValue)
    
    // Listen for regenerate events from content blocks on document level
    // (events bubble up from block elements)
    this.boundHandleRegenerateEvent = this.handleRegenerateEvent.bind(this)
    document.addEventListener("content-block:regenerate", this.boundHandleRegenerateEvent)
  }
  
  disconnect() {
    // Remove event listener from document
    if (this.boundHandleRegenerateEvent) {
      document.removeEventListener("content-block:regenerate", this.boundHandleRegenerateEvent)
    }
  }
  
  // Handle regenerate event from content block
  handleRegenerateEvent(event) {
    console.log("RegenerateBlockModalController: Received regenerate event", event.detail)
    const { blockId, draftId, contentAssetId } = event.detail
    
    // Only open if this modal is for the same block
    if (blockId === this.blockIdValue) {
      console.log("RegenerateBlockModalController: Opening modal for block:", blockId)
      this.open()
    } else {
      console.log("RegenerateBlockModalController: Ignoring event - block ID mismatch. Expected:", this.blockIdValue, "Got:", blockId)
    }
  }
  
  // Open the modal
  open() {
    this.element.classList.remove("hidden")
    document.body.style.overflow = "hidden" // Prevent background scrolling
    
    // Focus on custom prompt input
    if (this.hasCustomPromptTarget) {
      this.customPromptTarget.focus()
    }
  }
  
  // Close the modal
  close() {
    this.element.classList.add("hidden")
    document.body.style.overflow = "" // Restore scrolling
    
    // Reset form
    if (this.hasFormTarget) {
      this.formTarget.reset()
    }
    
    this.hideLoading()
    this.hideError()
  }
  
  // Handle form submission
  async submitForm(event) {
    event.preventDefault()
    
    // Validate that custom prompt (regeneration instructions) is provided
    const customPrompt = this.customPromptTarget.value.trim()
    if (!customPrompt) {
      this.showError("Regeneration instructions are required. Please provide specific instructions for how you want this block to be regenerated.")
      return
    }
    
    this.showLoading()
    this.hideError()
    this.submitButtonTarget.disabled = true
    
    const form = event.target
    const formData = new FormData(form)
    
    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: formData
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Show success message
        this.showSuccess("Block regenerated successfully")
        
        // Close modal and reload page after a short delay
        setTimeout(() => {
          this.close()
          window.location.reload()
        }, 1500)
      } else {
        this.showError(data.error || "Failed to regenerate block")
        this.submitButtonTarget.disabled = false
      }
    } catch (error) {
      console.error("Error regenerating block:", error)
      this.showError("An error occurred while regenerating the block")
      this.submitButtonTarget.disabled = false
    } finally {
      this.hideLoading()
    }
  }
  
  // Show loading state
  showLoading() {
    this.loadingTarget.classList.remove("hidden")
  }
  
  // Hide loading state
  hideLoading() {
    this.loadingTarget.classList.add("hidden")
  }
  
  // Show error message
  showError(message) {
    this.errorTarget.classList.remove("hidden")
    this.errorMessageTarget.textContent = message
  }
  
  // Hide error message
  hideError() {
    this.errorTarget.classList.add("hidden")
  }
  
  // Show success message (temporary)
  showSuccess(message) {
    // Replace loading with success message
    this.loadingTarget.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
        </svg>
        <span class="text-sm text-green-700">${message}</span>
      </div>
    `
    this.loadingTarget.classList.remove("hidden")
  }
}
